import { useEffect, useRef } from 'react';

import { isReactNativeWebView } from './webview-detection';

/**
 * Signals to React Native WebView that the page is fully ready
 * (data loaded, content rendered)
 *
 * @param route - Optional route identifier for debugging
 */
export const signalPageReady = (route?: string) => {
  if (typeof window === 'undefined') return;

  // Only send if in React Native WebView
  if (!isReactNativeWebView()) return;

  try {
    (
      window as Window & { ReactNativeWebView?: { postMessage: (msg: string) => void } }
    ).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'PAGE_READY',
        route: route || window.location.pathname,
        timestamp: Date.now(),
      }),
    );

    console.log('[PageReady] Signal sent', { route: route || window.location.pathname });
  } catch (error) {
    console.warn('[PageReady] Failed to send signal', error);
  }
};

/**
 * Hook to automatically signal page ready when loading completes
 *
 * @param isLoading - Loading state (from React Query, etc.)
 * @param route - Optional route identifier for debugging
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useQuery(...);
 * usePageReadySignal(isLoading);
 * ```
 */
/** Maximum ms to wait for loading before force-sending PAGE_READY (prevents infinite WebView spinner) */
const PAGE_READY_TIMEOUT_MS = 10000;

export const usePageReadySignal = (isLoading: boolean, route?: string) => {
  const hasSignaledRef = useRef(false);

  useEffect(() => {
    // Only signal once per mount, when loading completes
    if (!isLoading && !hasSignaledRef.current) {
      hasSignaledRef.current = true;

      // Small delay to ensure React has rendered the content
      requestAnimationFrame(() => {
        signalPageReady(route);
      });
    }
  }, [isLoading, route]);

  // Fallback: if loading never resolves (e.g. hung network request after background),
  // force-send PAGE_READY after timeout so the RN WebView doesn't wait forever.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!hasSignaledRef.current) {
        hasSignaledRef.current = true;
        console.warn('[PageReady] Timeout fallback triggered — forcing PAGE_READY signal', { route });
        signalPageReady(route);
      }
    }, PAGE_READY_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [route]);

  // Reset on unmount so re-mount can signal again
  useEffect(() => {
    return () => {
      hasSignaledRef.current = false;
    };
  }, []);
};

/**
 * Hook for pages with multiple loading states
 * Signals ready only when ALL loading states are false
 *
 * @param loadingStates - Array of loading states
 * @param route - Optional route identifier
 *
 * @example
 * ```tsx
 * const { isLoading: brandsLoading } = useBrands();
 * const { isLoading: profileLoading } = useProfile();
 * usePageReadySignalMultiple([brandsLoading, profileLoading]);
 * ```
 */
export const usePageReadySignalMultiple = (loadingStates: boolean[], route?: string) => {
  const isAnyLoading = loadingStates.some((state) => state);
  usePageReadySignal(isAnyLoading, route);
};

/**
 * Signals to React Native WebView that a brand has been created
 *
 * @param brandId - The ID of the created brand
 */
export const signalBrandCreated = (brandId: string) => {
  if (typeof window === 'undefined') return;

  // Only send if in React Native WebView
  if (!isReactNativeWebView()) return;

  try {
    (
      window as Window & { ReactNativeWebView?: { postMessage: (msg: string) => void } }
    ).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'BRAND_CREATED',
        brandId,
        timestamp: Date.now(),
      }),
    );

    console.log('[BrandCreated] Signal sent', { brandId });
  } catch (error) {
    console.warn('[BrandCreated] Failed to send signal', error);
  }
};

/**
 * Signals to React Native WebView to close the current tab/webview
 *
 * @param reason - Optional reason for closing (for debugging)
 */
export const signalCloseTab = (reason?: string) => {
  if (typeof window === 'undefined') return;

  // Only send if in React Native WebView
  if (!isReactNativeWebView()) return;

  try {
    (
      window as Window & { ReactNativeWebView?: { postMessage: (msg: string) => void } }
    ).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'CLOSE_TAB',
        reason: reason || 'user_dismissed',
        timestamp: Date.now(),
      }),
    );

    console.log('[CloseTab] Signal sent', { reason: reason || 'user_dismissed' });
  } catch (error) {
    console.warn('[CloseTab] Failed to send signal', error);
  }
};

/**
 * Signals to React Native WebView to navigate to the Inspiration Feed
 */
export const signalNavigateInspiration = () => {
  if (typeof window === 'undefined') return;

  // Only send if in React Native WebView
  if (!isReactNativeWebView()) return;

  try {
    (
      window as Window & { ReactNativeWebView?: { postMessage: (msg: string) => void } }
    ).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'NAVIGATE_INSPIRATION',
        timestamp: Date.now(),
      }),
    );

    console.log('[NavigateInspiration] Signal sent');
  } catch (error) {
    console.warn('[NavigateInspiration] Failed to send signal', error);
  }
};
